import { defaultAnnualRolloutPlan } from "../schedule/annual-rollout";
import {
  classifyCustomFamilies,
  generateInvestmentCurve,
  type RolloutSchedule,
} from "../schedule/investment-curve";
import { DEFAULT_RAMP_PROFILE, type RampUpProfile } from "../schedule/ramp-up";
import { compileRollout } from "../schedule/rollout-plan";
import { reportContractTermMonths } from "./report-derived";
import type { BasedOn, FitForOrg, ReviewRankingConfig } from "./review-types";
import { DEFAULT_REVIEW_RANKING_CONFIG } from "./review-types";
import type {
  CachedProjection,
  EvidenceReference,
  ProposedAssumption,
  ProposedValueStream,
  RolloutPlan,
} from "./types";

/** Decode common HTML entities that LLMs sometimes produce */
function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// ── Expression Evaluator ──────────────────────────────────────

/**
 * Evaluate a formula expression with assumption + context variable substitution.
 * Supports: a.<key>, c.<key>, basic arithmetic (+, -, *, /, parentheses).
 */
export function evaluateExpression(
  expr: string,
  assumptions: Record<string, number>,
  context: Record<string, number>,
): number {
  const resolved = expr
    .replace(/a\.(\w+)/g, (_, key: string) => String(assumptions[key] ?? 0))
    .replace(/c\.(\w+)/g, (_, key: string) => String(context[key] ?? 0));

  if (!/^[\d\s+\-*/().]+$/.test(resolved)) return 0;

  try {
    const fn = new Function(`"use strict"; return (${resolved});`);
    const result = fn() as unknown;
    const num = Number(result);
    return Number.isFinite(num) ? num : 0;
  } catch {
    return 0;
  }
}

// ── Types ─────────────────────────────────────────────────────

export interface ValueStreamResult {
  id: string;
  name: string;
  direction: "benefit" | "cost";
  value: number;
  formulaDescription: string;
  formulaExpr: string;
  costTiming?: "front-loaded" | "ongoing" | "scales-with-rollout";
}

export interface ComputeResult {
  projection: CachedProjection;
  valueStreamResults: ValueStreamResult[];
}

export interface SensitivityEntry {
  parameter: string;
  label: string;
  spread: number;
  baseROI: number;
  lowROI: number;
  highROI: number;
}

// ── Projection Computation ────────────────────────────────────

function resolvedAssumptionValue(
  assumption: ProposedAssumption,
  overrides?: Record<string, number>,
): number {
  const value = overrides?.[assumption.id] ?? assumption.default;
  if (value == null) {
    throw new Error(
      `Cannot compute projection: assumption "${assumption.id}" requires input`,
    );
  }
  return value;
}

/** Net at one end of every unresolved assumption's range. User-supplied
 *  values (overrides) stay fixed — the band expresses uncertainty over what
 *  the user has NOT settled, not over their own figures. */
function scenarioNet(
  valueStreams: ProposedValueStream[],
  assumptions: ProposedAssumption[],
  activeAssumptionIds: ReadonlySet<string | undefined>,
  context: Record<string, number>,
  overrides: Record<string, number> | undefined,
  end: "low" | "high",
): number {
  const assumptionMap: Record<string, number> = {};
  for (const assumption of assumptions) {
    if (!activeAssumptionIds.has(assumption.id)) continue;
    assumptionMap[assumption.id] =
      overrides?.[assumption.id] ??
      (end === "low" ? assumption.range.low : assumption.range.high);
  }
  let net = 0;
  for (const vs of valueStreams) {
    if (vs.direction === "benefit" && (vs.cadence ?? "annual") !== "annual") {
      continue;
    }
    const value = evaluateExpression(vs.formulaExpr, assumptionMap, context);
    net += vs.direction === "benefit" ? value : -value;
  }
  return net;
}

export function computeCustomProjection(
  valueStreams: ProposedValueStream[],
  assumptions: ProposedAssumption[],
  context: Record<string, number>,
  overrides?: Record<string, number>,
  rolloutSource?: RolloutPlan | RampUpProfile,
  implementationCost = 0,
  rampProfile?: RampUpProfile,
): ComputeResult {
  const activeAssumptionIds = new Set(
    valueStreams.flatMap((valueStream) =>
      [...valueStream.formulaExpr.matchAll(/a\.(\w+)/g)].map(
        (match) => match[1],
      ),
    ),
  );
  const assumptionMap: Record<string, number> = {};
  for (const assumption of assumptions) {
    if (activeAssumptionIds.has(assumption.id)) {
      assumptionMap[assumption.id] = resolvedAssumptionValue(
        assumption,
        overrides,
      );
    }
  }

  let grossBenefit = 0;
  let totalCost = 0;
  const breakdown: Record<string, number> = {};
  const valueStreamResults: ValueStreamResult[] = [];

  for (const vs of valueStreams) {
    const value = evaluateExpression(vs.formulaExpr, assumptionMap, context);
    breakdown[vs.id] = value;
    valueStreamResults.push({
      id: vs.id,
      name: decodeEntities(vs.name),
      direction: vs.direction,
      value,
      formulaDescription: vs.formulaDescription,
      formulaExpr: vs.formulaExpr,
      costTiming: vs.costTiming,
    });
    if (vs.direction === "benefit") {
      if ((vs.cadence ?? "annual") === "annual") grossBenefit += value;
    } else {
      totalCost += value;
    }
  }

  const netROI = grossBenefit - totalCost;
  // The confidence range is the model evaluated at the ends of every
  // unresolved assumption's own band — the range the research actually
  // supports — never a cosmetic percentage of the net.
  const netAtLow = scenarioNet(
    valueStreams,
    assumptions,
    activeAssumptionIds,
    context,
    overrides,
    "low",
  );
  const netAtHigh = scenarioNet(
    valueStreams,
    assumptions,
    activeAssumptionIds,
    context,
    overrides,
    "high",
  );
  const confidenceRange = {
    low: Math.min(netAtLow, netAtHigh, netROI),
    high: Math.max(netAtLow, netAtHigh, netROI),
  };
  const roiPercent = totalCost > 0 ? (netROI / totalCost) * 100 : null;
  const contractTermMonths = reportContractTermMonths({
    assumptions,
    userResponses: overrides,
  });
  const effectiveRolloutSource =
    rolloutSource ?? defaultAnnualRolloutPlan(contractTermMonths / 12);
  const paybackMonths =
    totalCost > 0 || implementationCost > 0
      ? computeCadenceAwarePayback(
          valueStreams,
          breakdown,
          effectiveRolloutSource,
          implementationCost,
          rampProfile,
          contractTermMonths,
        )
      : null;
  const year1Value =
    computeYear1Value(valueStreams, breakdown, effectiveRolloutSource) -
    implementationCost;

  return {
    projection: {
      grossAnnualBenefit: grossBenefit,
      totalAnnualCost: totalCost,
      netROI,
      roiPercent,
      year1Value,
      steadyStateValue: netROI,
      paybackMonths,
      valueStreamBreakdown: breakdown,
      priorityContributions: {},
      confidenceLevel: "estimated",
      confidenceRange,
      modelType: "custom",
    },
    valueStreamResults,
  };
}

function computeCadenceAwarePayback(
  valueStreams: ProposedValueStream[],
  breakdown: Record<string, number>,
  rolloutSource: RolloutPlan | RampUpProfile,
  implementationCost: number,
  savedRampProfile: RampUpProfile | undefined,
  horizon: number,
): number | null {
  const rampProfile =
    "implementation_months" in rolloutSource
      ? rolloutSource
      : (savedRampProfile ?? DEFAULT_RAMP_PROFILE);
  const classified = classifyCustomFamilies(
    valueStreams.map((stream) => ({
      familyId: stream.id,
      familyName: stream.name,
      direction: stream.direction,
      monthlyValue:
        stream.direction === "benefit" &&
        stream.cadence &&
        stream.cadence !== "annual"
          ? (breakdown[stream.id] ?? 0)
          : (breakdown[stream.id] ?? 0) / 12,
      costTiming: stream.costTiming,
      cadence: stream.cadence,
    })),
    implementationCost,
  );
  const schedule: RolloutSchedule = {
    monthlyFractions: compileRollout(rolloutSource, horizon).monthlyFractions,
    source: "custom",
  };
  return generateInvestmentCurve(
    classified,
    rampProfile,
    schedule,
    horizon,
    horizon,
  ).paybackMonth;
}

function computeYear1Value(
  valueStreams: ProposedValueStream[],
  breakdown: Record<string, number>,
  rolloutSource: RolloutPlan | RampUpProfile,
): number {
  const fractions = compileRollout(rolloutSource, 12).monthlyFractions;
  const averageRollout = fractions.reduce((sum, value) => sum + value, 0) / 12;
  const releasedRollout = Math.max(0, ...fractions);

  return valueStreams.reduce((net, stream) => {
    const value = breakdown[stream.id] ?? 0;
    let year1: number;
    if (stream.direction === "benefit") {
      year1 =
        stream.cadence === "one-time"
          ? value
          : stream.cadence === "one-time-with-rollout"
            ? value * releasedRollout
            : value * averageRollout;
    } else {
      year1 =
        stream.costTiming === "scales-with-rollout"
          ? value * averageRollout
          : value;
    }
    return net + (stream.direction === "benefit" ? year1 : -year1);
  }, 0);
}

/**
 * The value streams that count toward the model's outputs — i.e. all streams
 * minus the ones the user has dismissed (`excludedValueStreamIds`). This is the
 * single definition of the dismissal filter; every runtime compute path feeds
 * its result to computeCustomProjection / computeSensitivity / addNPVToProjection
 * so totals never include a dismissed stream.
 */
export function getActiveValueStreams(definition: {
  valueStreams: ProposedValueStream[];
  excludedValueStreamIds?: string[];
}): ProposedValueStream[] {
  const excluded = new Set(definition.excludedValueStreamIds ?? []);
  if (excluded.size === 0) return definition.valueStreams;
  return definition.valueStreams.filter((vs) => !excluded.has(vs.id));
}

// ── Sensitivity Analysis ──────────────────────────────────────

export function computeSensitivity(
  valueStreams: ProposedValueStream[],
  assumptions: ProposedAssumption[],
  context: Record<string, number>,
  overrides?: Record<string, number>,
): SensitivityEntry[] {
  const baseResult = computeCustomProjection(
    valueStreams,
    assumptions,
    context,
    overrides,
  );
  const baseROI = baseResult.projection.netROI;
  const entries: SensitivityEntry[] = [];

  for (const a of assumptions) {
    const lowOverrides = { ...overrides, [a.id]: a.range.low };
    const highOverrides = { ...overrides, [a.id]: a.range.high };

    const lowROI = computeCustomProjection(
      valueStreams,
      assumptions,
      context,
      lowOverrides,
    ).projection.netROI;
    const highROI = computeCustomProjection(
      valueStreams,
      assumptions,
      context,
      highOverrides,
    ).projection.netROI;

    const spread = Math.abs(highROI - lowROI);
    if (spread > 0) {
      entries.push({
        parameter: a.id,
        label: decodeEntities(a.name),
        spread,
        baseROI,
        lowROI,
        highROI,
      });
    }
  }

  return entries.sort((a, b) => b.spread - a.spread);
}

// ── Review-First Ranking ──────────────────────────────────────

export function computeAssumptionWeakness(
  basedOn: BasedOn,
  fitForOrg: FitForOrg,
  whatToConfirm: string | null,
  config: ReviewRankingConfig = DEFAULT_REVIEW_RANKING_CONFIG,
): number {
  let score = 0;
  score += config.basedOnWeights[basedOn] ?? 0;
  score += config.fitForOrgWeights[fitForOrg] ?? 0;
  score += whatToConfirm?.trim() ? config.unconfirmedWeight : 0;
  return Math.max(0, Math.min(1, score));
}

export function computeReviewFirstIds(
  assumptions: ProposedAssumption[],
  sensitivityEntries: SensitivityEntry[],
): string[] {
  const maxSpread = Math.max(
    ...sensitivityEntries.map((e) => Math.abs(e.spread)),
    1,
  );
  const materialityMap = new Map(
    sensitivityEntries.map((e) => [
      e.parameter,
      Math.abs(e.spread) / maxSpread,
    ]),
  );

  const ranked = assumptions
    .filter((a) => a.basedOn && a.fitForOrg)
    .map((a) => {
      const materiality = materialityMap.get(a.id) ?? 0;
      const weakness = computeAssumptionWeakness(
        a.basedOn as BasedOn,
        a.fitForOrg as FitForOrg,
        a.whatToConfirm ?? null,
      );
      return { id: a.id, priority: materiality * weakness };
    })
    .filter((r) => r.priority > 0)
    .sort((a, b) => b.priority - a.priority);

  return ranked.slice(0, 3).map((r) => r.id);
}

// ── Mapping for existing proforma components ──────────────────

export interface MappedAssumptionDef {
  assumption_id: string;
  assumption_name: string;
  description: string | null;
  default_value: number | null;
  min_value: number | null;
  max_value: number | null;
  step_value: number | null;
  unit: string | null;
  scenarioDefaults: Record<string, number> | null;
  /** The evidence basis for this assumption (e.g. "peer_benchmark", "vendor_claim"). */
  basedOn: string | null;
  /** ID of the value stream this assumption feeds into (matches ProposedValueStream.id). */
  linkedValueStream: string;
}

export interface MappedExplanation {
  assumption_id: string;
  computed_value: number | null;
  explanation_lines: string[];
  sources: EvidenceReference[];
  definition?: string;
  derivation?: string;
}

export function isPercentLike(
  format?: string | null,
  unit?: string | null,
): boolean {
  const formatStr = (format ?? "").trim().toLowerCase();
  const unitStr = (unit ?? "").trim().toLowerCase();
  return (
    formatStr === "percent" ||
    formatStr === "percentage" ||
    formatStr === "percent (decimal)" ||
    unitStr === "percent" ||
    unitStr === "percentage" ||
    unitStr === "%" ||
    unitStr === "percent (decimal)"
  );
}

function mapUnit(unit: string, format: string): string | null {
  if (isPercentLike(format, unit)) return "%";
  if (format === "currency" || unit === "dollars") return "$";
  if (unit === "hours") return "hours";
  if (unit === "minutes") return "minutes";
  return null;
}

export function mapToAssumptionDefs(
  assumptions: ProposedAssumption[],
): MappedAssumptionDef[] {
  return assumptions.map((a) => ({
    assumption_id: a.id,
    assumption_name: decodeEntities(a.name),
    description: a.description ? decodeEntities(a.description) : a.description,
    default_value: a.default,
    min_value: a.range.low,
    max_value: a.range.high,
    step_value: a.range.step,
    unit: mapUnit(a.unit, a.format),
    scenarioDefaults: null,
    basedOn: a.basedOn ?? null,
    linkedValueStream: a.linkedValueStream,
  }));
}

export function mapToExplanations(
  assumptions: ProposedAssumption[],
): MappedExplanation[] {
  return assumptions.map((a) => ({
    assumption_id: a.id,
    computed_value: a.default,
    explanation_lines: [a.explanation.derivation],
    sources: a.sources,
    definition: a.explanation.definition,
    derivation: a.explanation.derivation,
  }));
}
